import React, { useState } from 'react'
import Grid from '@material-ui/core/Grid'

import { Dialog, snackbar } from 'components'
import CustomTextField from 'components/CustomTextField'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'

import { Tag, CreateTagPayload, CreateTagResponse } from '../../types'
import { createTag } from '../../api'
import { TAG_GROUPS } from '../../consts'
import { TagAssignmentOptions, TagLoadMode } from '../TagAssignmentOptions'

const rules = {
  title: [
    {
      validate: (title: string) => title.length <= 100,
      message: 'No puede superar los 100 caracteres',
    },
  ],
  description: [
    {
      validate: (description: string) => description.length <= 200,
      message: 'No puede superar los 200 caracteres',
    },
  ],
}

interface CreateTagDialogProps {
  handleClose: () => void
  onDone: (newTag: Tag) => void
  // eslint-disable-next-line react/require-default-props
  tagGroup?: string
}

const CreateTagDialog: React.FC<CreateTagDialogProps> = ({
  handleClose,
  onDone,
  tagGroup = undefined,
}) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [active, setActive] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [loadMode, setLoadMode] = useState<TagLoadMode>('csv')
  const [subscriptionDomainRestricted, setSubscriptionDomainRestricted] = useState(false)
  const [subscriptionRequiredDomain, setSubscriptionRequiredDomain] = useState('')

  const [createTagApi, isLoading] = useApiCall<CreateTagPayload, CreateTagResponse>(createTag)

  const handleTitleChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setTitle(e.target.value)
  const handleDescriptionChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setDescription(e.target.value)
  const handleActiveChange: React.ChangeEventHandler<HTMLInputElement> = () => setActive(!active)
  const handleSubscriptionRequiredDomainChange: React.ChangeEventHandler<HTMLInputElement> = e =>
    setSubscriptionRequiredDomain(e.target.value)

  const fields = { title, description }
  const { isAnyFieldEmpty, hasErrors, errors } = useCheckFormErrors(fields, rules)

  const isCsvTab = tagGroup === TAG_GROUPS.FROM_CSV
  const isSubscriptionMode = isCsvTab && loadMode === 'subscription'

  // eslint-disable-next-line no-shadow
  const validateCSV = (file: File): Promise<string | null> => {
    return new Promise(resolve => {
      if (!file.name.endsWith('.csv')) {
        resolve('Solo se permiten archivos .csv')
        return
      }
      const reader = new FileReader()
      reader.onload = () => {
        const text = reader.result as string
        const lines = text
          .split('\n')
          .map(line => line.trim())
          .filter(line => line !== '')
        if (lines.length === 0) {
          resolve('El archivo está vacío.')
          return
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        const invalidEmails = lines.filter(line => !emailRegex.test(line.trim()))

        if (invalidEmails.length > 0) {
          resolve('El archivo contiene correos inválidos')
          return
        }
        resolve(null)
      }
      reader.onerror = () => {
        resolve('Error al leer el archivo')
      }
      reader.readAsText(file)
    })
  }

  // eslint-disable-next-line no-shadow
  const handleFileChange = async (file: File) => {
    const error = await validateCSV(file)
    setFileError(error)
    if (!error) setFile(file)
    else setFile(null)
  }

  const subscriptionDomainInvalid =
    isSubscriptionMode && subscriptionDomainRestricted && !subscriptionRequiredDomain.trim()

  const isSubmitDisabled =
    isAnyFieldEmpty ||
    hasErrors ||
    isLoading ||
    subscriptionDomainInvalid ||
    (isCsvTab && !isSubscriptionMode && (!file || !!fileError))

  const handleLoadModeChange = (mode: TagLoadMode) => {
    setLoadMode(mode)
    if (mode === 'subscription') {
      setActive(true)
    }
  }

  const onCreateTag = async () => {
    if (!title || !description) return
    if (subscriptionDomainInvalid) {
      snackbar.show('Debés indicar el dominio requerido')
      return
    }

    const tag: CreateTagPayload = {
      title,
      description,
      active,
      file: isCsvTab && !isSubscriptionMode ? file : null,
      group: tagGroup,
      ...(isSubscriptionMode
        ? {
            subscriptionEnabled: true,
            subscriptionDomainRestricted,
            subscriptionRequiredDomain: subscriptionDomainRestricted
              ? subscriptionRequiredDomain.trim()
              : null,
          }
        : {}),
    }

    try {
      const { tag: newTag } = await createTagApi(tag)
      onDone(newTag)
    } catch (err) {
      if (err instanceof Error && 'status' in err && err.status === 400) {
        snackbar.show(err.message)
      } else {
        snackbar.show('No se pudo crear la etiqueta. Intente de nuevo.')
      }
    }
  }

  return (
    <Dialog
      title="Nueva etiqueta (tag)"
      isOpen
      showActions
      onCancel={handleClose}
      isLoading={isLoading}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled }}
      onAccept={onCreateTag}
    >
      <Grid container spacing={3}>
        <Grid item xs={11}>
          <CustomTextField
            label="Título"
            value={title}
            fullWidth
            onChange={handleTitleChange}
            disabled={isLoading}
            required
            maxLength={100}
            error={errors.title.hasError}
            helperText={errors.title.message}
          />
        </Grid>
        <Grid item xs={11}>
          <CustomTextField
            label="Descripción"
            value={description}
            fullWidth
            onChange={handleDescriptionChange}
            disabled={isLoading}
            required
            maxLength={200}
            error={errors.description.hasError}
            helperText={errors.description.message}
          />
        </Grid>
        <Grid item xs={11}>
          <CustomFormControlLabel
            control={<CustomCheckbox onChange={handleActiveChange} checked={active} />}
            label="Activo"
          />
        </Grid>
        {isCsvTab && (
          <TagAssignmentOptions
            loadMode={loadMode}
            onLoadModeChange={handleLoadModeChange}
            file={file}
            fileError={fileError}
            onFileChange={handleFileChange}
            subscriptionDomainRestricted={subscriptionDomainRestricted}
            onSubscriptionDomainRestrictedChange={() =>
              setSubscriptionDomainRestricted(!subscriptionDomainRestricted)
            }
            subscriptionRequiredDomain={subscriptionRequiredDomain}
            onSubscriptionRequiredDomainChange={handleSubscriptionRequiredDomainChange}
            isLoading={isLoading}
          />
        )}
      </Grid>
    </Dialog>
  )
}

export { CreateTagDialog }
